/* CertificatesTab — Admin sub-tab for issuing & managing student certificates.
 *
 *   - Eligible view: shows enrolled students per program with their lesson
 *     completion %; admins pick rows + bulk-issue (or single-issue from any %).
 *   - Issued view: searchable list of all issued certificates with quick
 *     download / verify / revoke actions.
 *
 * All requests go through /api/academy/admin/certificates/* with cookie auth. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Award, RefreshCw, Search, ShieldCheck, ShieldOff, Download,
  Send, CheckSquare, Square, ExternalLink, Plus, X,
} from 'lucide-react';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const api = axios.create({ baseURL: API, withCredentials: true });

const DEFAULT_SIG_NAME = 'Dr. Aisha Kelechi';
const DEFAULT_SIG_TITLE = 'Director of ZHELTA Academy';

function StatusPill({ status }) {
  const cfg = status === 'active'
    ? { bg: 'bg-emerald-50', fg: 'text-emerald-700', border: 'border-emerald-200', label: 'Active' }
    : { bg: 'bg-red-50', fg: 'text-red-700', border: 'border-red-200', label: 'Revoked' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider text-[10px] ${cfg.bg} ${cfg.fg} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

export default function CertificatesTab({ programs }) {
  const [view, setView] = useState('eligible'); // 'eligible' | 'issued'
  const [program, setProgram] = useState(programs?.[0]?.slug || '');
  const [eligible, setEligible] = useState([]);
  const [issued, setIssued] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [issueModal, setIssueModal] = useState(null); // { rows: [...] } when bulk
  const [manualOpen, setManualOpen] = useState(false);

  // Pick first program once programs load.
  useEffect(() => {
    if (!program && programs?.length) setProgram(programs[0].slug);
  }, [programs, program]);

  const reloadEligible = useCallback(async () => {
    if (!program) return;
    setLoading(true);
    try {
      const { data } = await api.get('/api/academy/admin/certificates/eligible', { params: { program_slug: program } });
      setEligible(data.rows || []);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  }, [program]);

  const reloadIssued = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (program) params.program_slug = program;
      if (filterStatus) params.status = filterStatus;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get('/api/academy/admin/certificates', { params });
      setIssued(data.rows || []);
    } finally {
      setLoading(false);
    }
  }, [program, filterStatus, search]);

  useEffect(() => {
    if (view === 'eligible') reloadEligible();
    else reloadIssued();
  }, [view, reloadEligible, reloadIssued]);

  const toggleSelect = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === eligible.length) setSelected(new Set());
    else setSelected(new Set(eligible.map((e) => e.user_id)));
  };

  const programName = useMemo(() => {
    const p = (programs || []).find((x) => x.slug === program);
    return p?.name || program;
  }, [programs, program]);

  return (
    <div data-testid="academy-admin-certificates-tab">
      {/* Header strip */}
      <div className="bg-white rounded-2xl border border-[#E5E5E7] p-5 mb-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-[#c9a227]" />
          <span className="text-[13px] font-bold tracking-[0.18em] uppercase text-[#0e0e10]">Certificates</span>
        </div>

        <div className="inline-flex rounded-full border border-[#E5E5E7] overflow-hidden">
          <button
            type="button"
            onClick={() => setView('eligible')}
            data-testid="certs-view-eligible"
            className={`px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider ${view === 'eligible' ? 'bg-[#0e0e10] text-white' : 'text-[#6E6E73]'}`}
          >
            Eligible students
          </button>
          <button
            type="button"
            onClick={() => setView('issued')}
            data-testid="certs-view-issued"
            className={`px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider ${view === 'issued' ? 'bg-[#0e0e10] text-white' : 'text-[#6E6E73]'}`}
          >
            Issued certificates
          </button>
        </div>

        <select
          value={program}
          onChange={(e) => setProgram(e.target.value)}
          data-testid="certs-filter-program"
          className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 outline-none focus:border-[#c9a227] max-w-[260px]"
        >
          {(programs || []).map((p) => (
            <option key={p.slug} value={p.slug}>{p.name}</option>
          ))}
        </select>

        {view === 'issued' && (
          <>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="certs-filter-status"
              className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 outline-none focus:border-[#c9a227]"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="revoked">Revoked</option>
            </select>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#6E6E73] absolute left-3 top-2.5" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email or cert ID"
                data-testid="certs-search"
                className="text-[12px] rounded-full border border-[#E5E5E7] pl-8 pr-3 py-1.5 outline-none focus:border-[#c9a227] w-[260px]"
              />
            </div>
          </>
        )}

        <button
          onClick={view === 'eligible' ? reloadEligible : reloadIssued}
          data-testid="certs-refresh"
          className="ml-auto inline-flex items-center gap-1 text-[12px] text-[#6E6E73] hover:text-[#c9a227]"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>

        <button
          type="button"
          onClick={() => setManualOpen(true)}
          data-testid="certs-manual-issue-btn"
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
          style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}
        >
          <Plus className="w-3.5 h-3.5" /> Issue manually
        </button>

        {view === 'eligible' && selected.size > 0 && (
          <button
            type="button"
            onClick={() => setIssueModal({
              rows: eligible.filter((r) => selected.has(r.user_id)),
              programSlug: program,
              programName,
            })}
            data-testid="certs-bulk-issue-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
            style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}
          >
            <Send className="w-3.5 h-3.5" /> Issue to {selected.size} selected
          </button>
        )}
      </div>

      {view === 'eligible' ? (
        <EligibleView
          rows={eligible}
          selected={selected}
          onToggle={toggleSelect}
          onToggleAll={toggleAll}
          onIssueOne={(row) => setIssueModal({ rows: [row], programSlug: program, programName })}
        />
      ) : (
        <IssuedView rows={issued} onChanged={reloadIssued} />
      )}

      {issueModal && (
        <IssueModal
          {...issueModal}
          onClose={() => setIssueModal(null)}
          onIssued={() => { setIssueModal(null); reloadEligible(); }}
        />
      )}

      {manualOpen && (
        <ManualIssueModal
          programs={programs}
          defaultProgramSlug={program}
          onClose={() => setManualOpen(false)}
          onIssued={() => {
            setManualOpen(false);
            if (view === 'eligible') reloadEligible(); else reloadIssued();
          }}
        />
      )}
    </div>
  );
}

function EligibleView({ rows, selected, onToggle, onToggleAll }) {
  if (!rows.length) {
    return (
      <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center text-[#6E6E73]">
        <Award className="w-10 h-10 text-[#c9a227] mx-auto mb-3" />
        <div className="text-[14px] font-bold text-[#1D1D1F] mb-1">No enrolled students for this program</div>
        <p className="text-[12.5px] mb-3">Once students enrol they'll appear here with their lesson-completion progress.</p>
        <p className="text-[11.5px] text-[#6E6E73]">
          Need to issue a certificate without enrolment? Use{' '}
          <span className="font-bold text-[#a8871a]">Issue manually</span> in the toolbar above.
        </p>
      </div>
    );
  }
  const allSelected = selected.size === rows.length;

  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E7] overflow-hidden">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="bg-[#FAFAFA] text-[10.5px] uppercase tracking-[0.18em] text-[#6E6E73]">
            <th className="px-3 py-2 text-left w-8">
              <button onClick={onToggleAll} data-testid="certs-toggle-all">
                {allSelected
                  ? <CheckSquare className="w-4 h-4 text-[#c9a227]" />
                  : <Square className="w-4 h-4 text-[#6E6E73]" />}
              </button>
            </th>
            <th className="px-3 py-2 text-left">Student</th>
            <th className="px-3 py-2 text-left">Email</th>
            <th className="px-3 py-2 text-left">Tier</th>
            <th className="px-3 py-2 text-left w-[200px]">Completion</th>
            <th className="px-3 py-2 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isSelected = selected.has(r.user_id);
            const eligible = r.completion_pct >= 100;
            return (
              <tr key={r.user_id}
                className={`border-t border-[#E5E5E7] ${isSelected ? 'bg-[#fff8e7]' : ''}`}
                data-testid={`certs-eligible-row-${r.user_id}`}>
                <td className="px-3 py-2.5 align-middle">
                  <button onClick={() => onToggle(r.user_id)} data-testid={`certs-toggle-${r.user_id}`}>
                    {isSelected
                      ? <CheckSquare className="w-4 h-4 text-[#c9a227]" />
                      : <Square className="w-4 h-4 text-[#6E6E73]" />}
                  </button>
                </td>
                <td className="px-3 py-2.5 font-bold text-[#0e0e10]">{r.user_name}</td>
                <td className="px-3 py-2.5 font-mono text-[12px] text-[#6E6E73]">{r.user_email}</td>
                <td className="px-3 py-2.5 text-[12px] text-[#6E6E73] capitalize">{r.tier_id || '—'}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-[#f0f0f3] overflow-hidden">
                      <div className="h-full"
                        style={{
                          width: `${Math.max(2, r.completion_pct)}%`,
                          background: eligible
                            ? 'linear-gradient(90deg,#10b981 0%,#059669 100%)'
                            : 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)',
                        }} />
                    </div>
                    <span className={`text-[11px] font-bold tabular-nums ${eligible ? 'text-emerald-700' : 'text-[#a8871a]'}`}>
                      {r.completion_pct}%
                    </span>
                    <span className="text-[11px] text-[#6E6E73]">
                      ({r.completed_lessons}/{r.total_lessons})
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button
                    type="button"
                    onClick={() => onToggle(r.user_id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border border-[#c9a227] text-[#a8871a] hover:bg-[#fff8e7]"
                    data-testid={`certs-quick-select-${r.user_id}`}
                  >
                    Select
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function IssuedView({ rows, onChanged }) {
  if (!rows.length) {
    return (
      <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center text-[#6E6E73]">
        <ShieldCheck className="w-10 h-10 text-[#c9a227] mx-auto mb-3" />
        <div className="text-[14px] font-bold text-[#1D1D1F] mb-1">No certificates issued yet</div>
        <p className="text-[12.5px]">Switch to "Eligible students" and issue your first certificate.</p>
      </div>
    );
  }

  const revoke = async (row) => {
    const reason = window.prompt(`Revoke certificate ${row.cert_id} for ${row.user_name}? Optional reason:`);
    if (reason === null) return;
    await api.post(`/api/academy/admin/certificates/${row.cert_id}/revoke`, { reason });
    onChanged();
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E7] overflow-hidden">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="bg-[#FAFAFA] text-[10.5px] uppercase tracking-[0.18em] text-[#6E6E73]">
            <th className="px-3 py-2 text-left">Cert ID</th>
            <th className="px-3 py-2 text-left">Student</th>
            <th className="px-3 py-2 text-left">Program</th>
            <th className="px-3 py-2 text-left">Issued</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[#E5E5E7]" data-testid={`certs-issued-row-${r.cert_id}`}>
              <td className="px-3 py-2.5 font-mono text-[12px] text-[#0e0e10]">{r.cert_id}</td>
              <td className="px-3 py-2.5">
                <div className="font-bold text-[#0e0e10]">{r.user_name}</div>
                <div className="text-[11px] text-[#6E6E73] font-mono">{r.user_email}</div>
              </td>
              <td className="px-3 py-2.5 text-[12.5px]">{r.program_name}</td>
              <td className="px-3 py-2.5 text-[12px] text-[#6E6E73]">
                {new Date(r.issued_date).toLocaleString(undefined, { dateStyle: 'medium' })}
              </td>
              <td className="px-3 py-2.5"><StatusPill status={r.status} /></td>
              <td className="px-3 py-2.5 text-right whitespace-nowrap">
                <a href={r.pdf_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border border-[#E5E5E7] text-[#0e0e10] hover:border-[#c9a227] mr-2"
                  data-testid={`certs-download-${r.cert_id}`}>
                  <Download className="w-3 h-3" /> PDF
                </a>
                <a href={r.verify_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border border-[#E5E5E7] text-[#0e0e10] hover:border-[#c9a227] mr-2"
                  data-testid={`certs-verify-${r.cert_id}`}>
                  <ExternalLink className="w-3 h-3" /> Verify
                </a>
                {r.status === 'active' && (
                  <button onClick={() => revoke(r)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border border-red-200 text-red-700 hover:bg-red-50"
                    data-testid={`certs-revoke-${r.cert_id}`}>
                    <ShieldOff className="w-3 h-3" /> Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IssueModal({ rows, programSlug, programName, onClose, onIssued }) {
  const [signatureName, setSignatureName] = useState(DEFAULT_SIG_NAME);
  const [signatureTitle, setSignatureTitle] = useState(DEFAULT_SIG_TITLE);
  const [completionDate, setCompletionDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(null);

  const isBulk = rows.length > 1;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors([]);
    setSuccess(null);
    try {
      if (isBulk) {
        const { data } = await api.post('/api/academy/admin/certificates/bulk-issue', {
          program_slug: programSlug,
          user_ids: rows.map((r) => r.user_id),
          signature_name: signatureName,
          signature_title: signatureTitle,
          completion_date: completionDate,
        });
        setErrors(data.errors || []);
        setSuccess(`Issued ${data.summary?.issued ?? 0} certificate(s).`);
        if ((data.errors || []).length === 0) {
          setTimeout(onIssued, 900);
        }
      } else {
        await api.post('/api/academy/admin/certificates/issue', {
          user_id: rows[0].user_id,
          program_slug: programSlug,
          signature_name: signatureName,
          signature_title: signatureTitle,
          completion_date: completionDate,
        });
        setSuccess(`Certificate issued to ${rows[0].user_name}.`);
        setTimeout(onIssued, 900);
      }
    } catch (err) {
      setErrors([{ user_id: '—', error: err.response?.data?.detail || err.message }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="certs-issue-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="bg-[#0e0e10] text-white px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-[#f5a623]" />
            <span className="text-[13px] font-bold tracking-[0.18em] uppercase">
              {isBulk ? `Bulk-issue (${rows.length})` : 'Issue certificate'}
            </span>
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white text-[13px]">Cancel</button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Program</div>
            <div className="text-[14px] font-bold text-[#0e0e10]">{programName}</div>
          </div>

          <div>
            <div className="text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">
              Recipients ({rows.length})
            </div>
            <div className="rounded-xl border border-[#E5E5E7] max-h-[160px] overflow-auto bg-[#FAFAFA]">
              <ul className="divide-y divide-[#E5E5E7]">
                {rows.map((r) => (
                  <li key={r.user_id} className="px-3 py-2 flex items-center justify-between text-[12.5px]">
                    <span className="font-bold text-[#0e0e10]">{r.user_name}</span>
                    <span className="font-mono text-[11.5px] text-[#6E6E73]">{r.user_email}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Completion date</span>
              <input
                type="date"
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                data-testid="certs-completion-date"
                className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]"
              />
            </label>
            <label className="block">
              <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Signed by</span>
              <input
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                required
                data-testid="certs-signature-name"
                className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]"
              />
            </label>
          </div>

          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Signatory title</span>
            <input
              value={signatureTitle}
              onChange={(e) => setSignatureTitle(e.target.value)}
              data-testid="certs-signature-title"
              className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]"
            />
          </label>

          {success && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12.5px] text-emerald-800"
              data-testid="certs-issue-success">
              {success}
            </div>
          )}
          {errors.length > 0 && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[12px] text-red-700 space-y-1">
              {errors.map((e, i) => (
                <div key={i}><strong className="font-mono">{e.user_id}</strong>: {e.error}</div>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-4 bg-[#FAFAFA] flex items-center justify-end gap-2 border-t border-[#E5E5E7]">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#6E6E73] hover:text-[#0e0e10]">
            Close
          </button>
          <button type="submit" disabled={busy}
            data-testid="certs-issue-confirm"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
            <Send className="w-3.5 h-3.5" />
            {busy ? 'Issuing…' : isBulk ? `Issue ${rows.length} certs` : 'Issue & email'}
          </button>
        </div>
      </form>
    </div>
  );
}


/* ManualIssueModal — pick ANY user (search students/instructors) + ANY program
 * and issue a cert. Bypasses the eligibility check for special cases like
 * legacy completions or bootcamp certificates not tracked via lesson progress. */
function ManualIssueModal({ programs, defaultProgramSlug, onClose, onIssued }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null); // { id, email, name }
  const [programSlug, setProgramSlug] = useState(defaultProgramSlug || (programs?.[0]?.slug ?? ''));
  const [signatureName, setSignatureName] = useState(DEFAULT_SIG_NAME);
  const [signatureTitle, setSignatureTitle] = useState(DEFAULT_SIG_TITLE);
  const [completionDate, setCompletionDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Load all students once on open. Cohort sizes are small enough that
  // client-side filtering is fine — no debounced server calls needed.
  useEffect(() => {
    let active = true;
    setLoadingUsers(true);
    api.get('/api/academy/admin/users', { params: { role: 'student' } })
      .then(({ data }) => { if (active) setUsers(data.rows || []); })
      .catch(() => { if (active) setUsers([]); })
      .finally(() => { if (active) setLoadingUsers(false); });
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users.slice(0, 25);
    return users.filter((u) => (
      (u.email || '').toLowerCase().includes(q) ||
      (u.name || '').toLowerCase().includes(q)
    )).slice(0, 25);
  }, [search, users]);

  const programName = useMemo(() => {
    const p = (programs || []).find((x) => x.slug === programSlug);
    return p?.name || programSlug;
  }, [programs, programSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!picked || !programSlug) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/api/academy/admin/certificates/issue', {
        user_id: picked.id,
        program_slug: programSlug,
        signature_name: signatureName,
        signature_title: signatureTitle,
        completion_date: completionDate,
      });
      setSuccess(`Certificate issued to ${picked.name || picked.email}.`);
      setTimeout(onIssued, 900);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to issue certificate');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="certs-manual-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden">
        <div className="bg-[#0e0e10] text-white px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-[#f5a623]" />
            <span className="text-[13px] font-bold tracking-[0.18em] uppercase">Issue certificate manually</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="text-white/70 hover:text-white" data-testid="certs-manual-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Step 1 — student picker */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1.5">
              1 · Choose student
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#6E6E73] absolute left-3 top-2.5" />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPicked(null); }}
                placeholder="Search by name or email"
                data-testid="certs-manual-search"
                className="w-full text-[13px] rounded-lg border border-[#E5E5E7] pl-8 pr-3 py-2 outline-none focus:border-[#c9a227]"
              />
            </div>

            {picked ? (
              <div className="mt-2 rounded-lg border border-[#c9a227] bg-[#fff8e7] px-3 py-2 flex items-center justify-between"
                data-testid="certs-manual-picked">
                <div>
                  <div className="text-[13px] font-bold text-[#0e0e10]">{picked.name || picked.email}</div>
                  <div className="text-[11.5px] font-mono text-[#6E6E73]">{picked.email}</div>
                </div>
                <button type="button" onClick={() => setPicked(null)}
                  className="text-[11px] font-bold uppercase tracking-wider text-[#a8871a] hover:text-[#0e0e10]">
                  Change
                </button>
              </div>
            ) : (
              <div className="mt-2 rounded-lg border border-[#E5E5E7] max-h-[180px] overflow-auto">
                {loadingUsers ? (
                  <div className="p-3 text-[12px] text-[#6E6E73]">Loading students…</div>
                ) : filtered.length === 0 ? (
                  <div className="p-3 text-[12px] text-[#6E6E73]">No students match.</div>
                ) : (
                  <ul className="divide-y divide-[#E5E5E7]">
                    {filtered.map((u) => (
                      <li key={u.id}>
                        <button type="button" onClick={() => setPicked(u)}
                          data-testid={`certs-manual-pick-${u.id}`}
                          className="w-full text-left px-3 py-2 hover:bg-[#FAFAFA] flex items-center justify-between gap-3">
                          <span className="font-bold text-[13px] text-[#0e0e10]">{u.name || u.email}</span>
                          <span className="font-mono text-[11.5px] text-[#6E6E73]">{u.email}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Step 2 — program */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1.5">
              2 · Program
            </div>
            <select
              value={programSlug}
              onChange={(e) => setProgramSlug(e.target.value)}
              data-testid="certs-manual-program"
              className="w-full text-[13px] rounded-lg border border-[#E5E5E7] px-3 py-2 outline-none focus:border-[#c9a227]"
            >
              {(programs || []).map((p) => (
                <option key={p.slug} value={p.slug}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Step 3 — details */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Completion date</span>
              <input
                type="date"
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                data-testid="certs-manual-date"
                className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]"
              />
            </label>
            <label className="block">
              <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Signed by</span>
              <input
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                required
                data-testid="certs-manual-sig-name"
                className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]"
              />
            </label>
          </div>
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Signatory title</span>
            <input
              value={signatureTitle}
              onChange={(e) => setSignatureTitle(e.target.value)}
              data-testid="certs-manual-sig-title"
              className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]"
            />
          </label>

          {/* Preview pill */}
          {picked && (
            <div className="rounded-xl bg-[#FAFAFA] border border-[#E5E5E7] px-4 py-3 text-[12.5px] text-[#0e0e10]"
              data-testid="certs-manual-preview">
              About to issue:{' '}
              <strong>{programName}</strong> certificate to{' '}
              <strong>{picked.name || picked.email}</strong>, dated{' '}
              <strong>{completionDate}</strong>, signed by{' '}
              <strong>{signatureName}</strong>.
            </div>
          )}

          {success && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12.5px] text-emerald-800"
              data-testid="certs-manual-success">{success}</div>
          )}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[12.5px] text-red-700"
              data-testid="certs-manual-error">{error}</div>
          )}
        </div>

        <div className="px-5 py-4 bg-[#FAFAFA] flex items-center justify-end gap-2 border-t border-[#E5E5E7]">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#6E6E73] hover:text-[#0e0e10]">
            Cancel
          </button>
          <button type="submit" disabled={busy || !picked || !programSlug}
            data-testid="certs-manual-confirm"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
            <Send className="w-3.5 h-3.5" />
            {busy ? 'Issuing…' : 'Issue & email'}
          </button>
        </div>
      </form>
    </div>
  );
}
